import type { GistItem } from "../../model/types";
import type {
  WorkspaceNavigationBuildOptions,
  WorkspaceNavigationNode,
  WorkspaceNavigationRow,
} from "./workspaceNavigationTypes";

function defaultCompareGists(a: GistItem, b: GistItem): number {
  return a.title.localeCompare(b.title) || a.id - b.id;
}

export function getUniqueWorkspaceGists(gists: GistItem[]): GistItem[] {
  const byId = new Map<number, GistItem>();
  gists.forEach((gist) => {
    if (typeof gist.id === "number") {
      byId.set(gist.id, gist);
    }
  });
  return Array.from(byId.values());
}

function hasValidParent(
  gist: GistItem,
  byId: Map<number, GistItem>,
): gist is GistItem & { parentId: number } {
  if (typeof gist.parentId !== "number") return false;
  if (!byId.has(gist.parentId)) return false;

  const visitedIds = new Set<number>([gist.id]);
  let currentParentId: number | undefined = gist.parentId;

  while (typeof currentParentId === "number") {
    if (visitedIds.has(currentParentId)) return false;
    visitedIds.add(currentParentId);

    const parent = byId.get(currentParentId);
    if (!parent || typeof parent.parentId !== "number") return true;
    currentParentId = parent.parentId;
  }

  return true;
}

export function buildWorkspaceGistTree(
  gists: GistItem[],
  options: WorkspaceNavigationBuildOptions = {},
): WorkspaceNavigationNode[] {
  const compareGists = options.sortGists || defaultCompareGists;
  const uniqueGists = getUniqueWorkspaceGists(gists).sort(compareGists);
  const byId = new Map(uniqueGists.map((gist) => [gist.id, gist]));
  const childrenByParentId = new Map<number, GistItem[]>();
  const roots: GistItem[] = [];

  uniqueGists.forEach((gist) => {
    if (hasValidParent(gist, byId)) {
      const siblings = childrenByParentId.get(gist.parentId) || [];
      siblings.push(gist);
      childrenByParentId.set(gist.parentId, siblings);
      return;
    }

    roots.push(gist);
  });

  childrenByParentId.forEach((children, parentId) => {
    childrenByParentId.set(parentId, children.sort(compareGists));
  });

  const buildNode = (gist: GistItem): WorkspaceNavigationNode => ({
    gist,
    children: (childrenByParentId.get(gist.id) || []).map(buildNode),
  });

  return roots.sort(compareGists).map(buildNode);
}

export function getWorkspaceNavigationPath(
  gists: GistItem[],
  currentGistId?: number | null,
): GistItem[] {
  if (typeof currentGistId !== "number") return [];

  const byId = new Map(getUniqueWorkspaceGists(gists).map((gist) => [gist.id, gist]));
  const path: GistItem[] = [];
  const visitedIds = new Set<number>();
  let current = byId.get(currentGistId);

  while (current && !visitedIds.has(current.id)) {
    visitedIds.add(current.id);
    path.unshift(current);

    if (typeof current.parentId !== "number") break;
    current = byId.get(current.parentId);
  }

  return path;
}

export function flattenWorkspaceGistTree(
  nodes: WorkspaceNavigationNode[],
  currentGistId?: number | null,
  allGists: GistItem[] = [],
): WorkspaceNavigationRow[] {
  const currentPathIds = new Set(
    getWorkspaceNavigationPath(allGists, currentGistId).map((gist) => gist.id),
  );
  const rows: WorkspaceNavigationRow[] = [];

  const visit = (
    node: WorkspaceNavigationNode,
    depth: number,
    isBelowCurrent: boolean,
  ) => {
    const isCurrent = node.gist.id === currentGistId;
    rows.push({
      gist: node.gist,
      depth,
      hasChildren: node.children.length > 0,
      isCurrent,
      isAncestorOfCurrent: currentPathIds.has(node.gist.id) && !isCurrent,
      isDescendantOfCurrent: isBelowCurrent,
    });

    node.children.forEach((child) => visit(child, depth + 1, isBelowCurrent || isCurrent));
  };

  nodes.forEach((node) => visit(node, 0, false));
  return rows;
}
